import { toast } from 'sonner';
import { FileSignature, RotateCcw } from 'lucide-react';
import {
  usePersonDocuments,
  useSetPersonDocument,
  useReopenPersonDocument,
} from '../../hooks/onboardingOps';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { ApiError } from '../../lib/api';
import { ItemStatusPill, canReopen } from './itemStatus';

/**
 * Per-person document assignment. Documents to sign are normally driven by
 * the role template, but not everyone signs everything - this lets HR tick
 * specific documents on / off for one person, and reopen a signed one.
 * Unpublished documents are shown but can't be assigned (the inductee would
 * get a "Sign: X" task that never opens).
 */
export function PersonDocumentsPanel({ personId }: { personId: string }) {
  const docs = usePersonDocuments(personId);
  const set = useSetPersonDocument(personId);
  const reopen = useReopenPersonDocument(personId);

  if (docs.isLoading || !docs.data) return null;
  if (docs.data.length === 0) return null;

  const assignedCount = docs.data.filter((d) => d.assigned).length;

  return (
    <CollapsibleSection
      icon={<FileSignature className="h-4 w-4 text-fg-muted" />}
      title="Documents to sign"
      trailing={`${assignedCount} assigned`}
    >
      <p className="mb-2 text-xs text-fg-muted">
        Tick the documents this person needs to sign. Role templates pre-select some; adjust per person here.
      </p>
      <ul className="divide-y divide-divider/10">
        {docs.data.map((d) => (
          <li key={d.id} className="flex items-center justify-between gap-2 py-2.5 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-fg">
              <span className="truncate">{d.title}</span>
              <ItemStatusPill status={d.status} />
              {!d.published ? (
                <Pill tone="warning" size="sm">
                  No version
                </Pill>
              ) : null}
            </span>
            <div className="flex shrink-0 items-center gap-3">
              {d.assigned && canReopen(d.status) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
                  loading={reopen.isPending}
                  onClick={async () => {
                    try {
                      await reopen.mutateAsync(d.id);
                      toast.success('Reopened');
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not reopen');
                    }
                  }}
                >
                  Reopen
                </Button>
              ) : null}
              <label className="flex items-center gap-2 text-fg-muted">
                <input
                  type="checkbox"
                  checked={d.assigned}
                  disabled={set.isPending || (!d.assigned && !d.published)}
                  onChange={async (e) => {
                    try {
                      await set.mutateAsync({ documentId: d.id, assigned: e.target.checked });
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not update');
                    }
                  }}
                />
                {d.assigned ? 'Assigned' : 'Not assigned'}
              </label>
            </div>
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  );
}
